import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaNewspaper, FaCalendarAlt, FaEye, FaStar, FaSearch,
  FaBullhorn, FaGraduationCap, FaLaptop, FaBible, FaChevronRight,
  FaClock, FaFire, FaBookOpen
} from 'react-icons/fa';
import noticiasMuralService from '../../services/noticiasMuralService';

// Mapeamento de ícones
const iconMap = {
  FaBullhorn: FaBullhorn,
  FaCalendarAlt: FaCalendarAlt,
  FaGraduationCap: FaGraduationCap,
  FaLaptop: FaLaptop,
  FaBible: FaBible,
  FaStar: FaStar,
  FaNewspaper: FaNewspaper,
};

/**
 * Página do Mural de Notícias - Estilo Portal
 */
const MuralNoticias = () => {
  const [noticias, setNoticias] = useState([]);
  const [portalData, setPortalData] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(''); // Estado local para o input
  const [searchTerm, setSearchTerm] = useState(''); // Estado para a busca efetiva (com debounce)
  const [categoriaAtiva, setCategoriaAtiva] = useState(null);
  const searchInputRef = useRef(null); // Ref para manter foco
  const debounceTimerRef = useRef(null); // Ref para o timer do debounce

  // Debounce da busca - atualiza searchTerm após 500ms de inatividade
  useEffect(() => {
    // Limpar timer anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Criar novo timer
    debounceTimerRef.current = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchInput]);

  // Carregar categorias
  useEffect(() => {
    const carregarCategorias = async () => {
      try {
        const response = await noticiasMuralService.listarCategorias();
        if (response.success) {
          setCategorias(response.data);
        }
      } catch (error) {
        console.error('Erro ao carregar categorias:', error);
      }
    };
    carregarCategorias();
  }, []);

  // Carregar notícias do portal (quando não há filtros)
  useEffect(() => {
    const carregarPortal = async () => {
      if (categoriaAtiva || searchTerm) return; // Não carregar se houver filtros
      
      try {
        setLoading(true);
        const response = await noticiasMuralService.getPortal();
        if (response.success) {
          setPortalData(response.data);
        }
      } catch (error) {
        console.error('Erro ao carregar portal:', error);
      } finally {
        setLoading(false);
      }
    };
    carregarPortal();
  }, [categoriaAtiva, searchTerm]);

  // Carregar notícias com filtros
  const carregarNoticias = useCallback(async () => {
    if (!categoriaAtiva && !searchTerm) return; // Usar portal se não houver filtros
    
    try {
      setLoading(true);
      const params = { limit: 30 };
      if (categoriaAtiva) params.categoria_id = categoriaAtiva;
      if (searchTerm) params.search = searchTerm;
      
      const response = await noticiasMuralService.listar(params);
      if (response.success) {
        setNoticias(response.data);
        setPortalData(null); // Limpar dados do portal quando buscar
      }
    } catch (error) {
      console.error('Erro ao carregar notícias:', error);
    } finally {
      setLoading(false);
    }
  }, [categoriaAtiva, searchTerm]);

  useEffect(() => {
    if (categoriaAtiva || searchTerm) {
      carregarNoticias();
    }
  }, [carregarNoticias, categoriaAtiva, searchTerm]);

  // Formatar data
  const formatarData = (dataString) => {
    if (!dataString) return '';
    const data = new Date(dataString);
    const agora = new Date();
    const diff = agora - data;
    const dias = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (dias === 0) {
      const horas = Math.floor(diff / (1000 * 60 * 60));
      if (horas === 0) {
        const minutos = Math.floor(diff / (1000 * 60));
        return minutos <= 1 ? 'Agora' : `${minutos}min`;
      }
      return `${horas}h`;
    }
    if (dias === 1) return 'Ontem';
    if (dias < 7) return `${dias}d`;
    
    return data.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  };

  // Renderizar ícone da categoria
  const renderCategIcon = (icone, cor, size = 'w-4 h-4') => {
    const IconComponent = iconMap[icone] || FaNewspaper;
    return <IconComponent className={size} style={{ color: cor }} />;
  };

  // Usar dados do portal ou filtrar da lista geral
  const destaquesNoticias = portalData?.destaque_hero || noticias.filter(n => n.destaque === 1).slice(0, 3);
  const eventosNoticias = portalData?.eventos || noticias.filter(n => n.categoria_nome === 'Eventos').slice(0, 4);
  const avisosNoticias = portalData?.avisos || noticias.filter(n => n.categoria_nome === 'Avisos Gerais').slice(0, 4);
  const novidadesNoticias = portalData?.novidades || noticias.filter(n => n.categoria_nome === 'Novidades do Sistema').slice(0, 4);
  const calendarioNoticias = portalData?.calendario || noticias.filter(n => n.categoria_nome === 'Calendário Acadêmico').slice(0, 5);
  const devocionalNoticia = portalData?.devocional?.[0] || noticias.find(n => n.categoria_nome === 'Devocional');
  const maisLidasNoticias = portalData?.mais_lidas || [...noticias].sort((a, b) => (b.visualizacoes || 0) - (a.visualizacoes || 0)).slice(0, 5);

  // Notícias que não são destaque (para "Últimas Notícias")
  const ultimasNoticias = noticias.filter(n => n.destaque !== 1).slice(0, 8);

  // Loading skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-12 bg-gray-300 rounded-xl w-full mb-6"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
              <div className="lg:col-span-2 h-80 bg-gray-300 rounded-xl"></div>
              <div className="space-y-4">
                <div className="h-[152px] bg-gray-300 rounded-xl"></div>
                <div className="h-[152px] bg-gray-300 rounded-xl"></div>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[1,2,3,4].map(i => (
                <div key={i} className="h-48 bg-gray-300 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header do Mural */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <FaNewspaper className="w-8 h-8" />
              <div>
                <h1 className="text-2xl font-bold">Mural de Notícias</h1>
                <p className="text-indigo-200 text-sm">Instituto Bíblico Ebenezer</p>
              </div>
            </div>
            
            {/* Busca */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-300 w-4 h-4" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Buscar notícias..."
                className="pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-indigo-200 focus:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/30 w-full sm:w-64"
              />
              {/* Indicador de carregamento durante a busca */}
              {searchInput !== searchTerm && searchInput && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                </div>
              )}
            </div>
          </div>
          
          {/* Categorias como tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2 scrollbar-hide">
            <button
              onClick={() => setCategoriaAtiva(null)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                !categoriaAtiva 
                  ? 'bg-white text-indigo-600' 
                  : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              Todas
            </button>
            {categorias.map(cat => (
              <button
                key={cat.id}
                onClick={() => setCategoriaAtiva(cat.id)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                  categoriaAtiva === cat.id
                    ? 'bg-white text-indigo-600'
                    : 'bg-white/10 hover:bg-white/20'
                }`}
              >
                {renderCategIcon(cat.icone, categoriaAtiva === cat.id ? cat.cor : '#fff', 'w-3.5 h-3.5')}
                {cat.nome}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        
        {/* ===== SEÇÃO HERO - DESTAQUES ===== */}
        {destaquesNoticias.length > 0 && !categoriaAtiva && (
          <section className="mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Destaque Principal */}
              <Link 
                to={`/mural/${destaquesNoticias[0]?.id}`}
                className="lg:col-span-2 relative group overflow-hidden rounded-2xl bg-gray-900 h-[280px] sm:h-[320px] lg:h-[400px]"
              >
                {destaquesNoticias[0]?.imagem_capa ? (
                  <img 
                    src={destaquesNoticias[0].imagem_capa} 
                    alt={destaquesNoticias[0].titulo}
                    className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500"
                  />
                ) : (
                  <div 
                    className="absolute inset-0 opacity-30"
                    style={{ 
                      background: `linear-gradient(135deg, ${destaquesNoticias[0]?.categoria_cor || '#6366f1'} 0%, #1e1b4b 100%)`
                    }}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span 
                      className="px-3 py-1 rounded-full text-xs font-semibold text-white"
                      style={{ backgroundColor: destaquesNoticias[0]?.categoria_cor || '#6366f1' }}
                    >
                      {destaquesNoticias[0]?.categoria_nome || 'Geral'}
                    </span>
                    <span className="flex items-center gap-1 text-amber-400 text-xs">
                      <FaStar className="w-3 h-3" />
                      Destaque
                    </span>
                  </div>
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2 group-hover:text-indigo-200 transition-colors line-clamp-2">
                    {destaquesNoticias[0]?.titulo}
                  </h2>
                  {destaquesNoticias[0]?.subtitulo && (
                    <p className="text-gray-300 text-sm lg:text-base line-clamp-2 hidden sm:block">
                      {destaquesNoticias[0].subtitulo}
                    </p>
                  )}
                  <div className="flex items-center gap-4 mt-3 text-gray-400 text-sm">
                    <span className="flex items-center gap-1">
                      <FaClock className="w-3 h-3" />
                      {formatarData(destaquesNoticias[0]?.data_publicacao)}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaEye className="w-3 h-3" />
                      {destaquesNoticias[0]?.visualizacoes || 0}
                    </span>
                  </div>
                </div>
              </Link>

              {/* Destaques Secundários */}
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
                {destaquesNoticias.slice(1, 3).map((noticia) => (
                  <Link
                    key={noticia.id}
                    to={`/mural/${noticia.id}`}
                    className="relative group overflow-hidden rounded-xl bg-gray-900 h-[160px] lg:h-[190px]"
                  >
                    {noticia.imagem_capa ? (
                      <img 
                        src={noticia.imagem_capa} 
                        alt={noticia.titulo}
                        className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500"
                      />
                    ) : (
                      <div 
                        className="absolute inset-0 opacity-40"
                        style={{ 
                          background: `linear-gradient(135deg, ${noticia.categoria_cor || '#6366f1'} 0%, #1e1b4b 100%)`
                        }}
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <span 
                        className="inline-block px-2 py-0.5 rounded text-xs font-medium text-white mb-2"
                        style={{ backgroundColor: noticia.categoria_cor || '#6366f1' }}
                      >
                        {noticia.categoria_nome || 'Geral'}
                      </span>
                      <h3 className="text-white font-semibold line-clamp-2 text-sm lg:text-base group-hover:text-indigo-200 transition-colors">
                        {noticia.titulo}
                      </h3>
                      <span className="text-gray-400 text-xs mt-1 flex items-center gap-1">
                        <FaClock className="w-3 h-3" />
                        {formatarData(noticia.data_publicacao)}
                      </span>
                    </div>
                  </Link>
                ))}

                {/* Placeholder se não houver destaques suficientes */}
                {destaquesNoticias.length === 1 && (
                  <>
                    <div className="rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center h-[160px] lg:h-[190px]">
                      <p className="text-indigo-400 text-sm">Mais notícias em breve</p>
                    </div>
                    <div className="rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center h-[160px] lg:h-[190px] hidden lg:flex">
                      <p className="text-purple-400 text-sm">Mais notícias em breve</p>
                    </div>
                  </>
                )}
                {destaquesNoticias.length === 2 && (
                  <div className="rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center h-[160px] lg:h-[190px]">
                    <p className="text-purple-400 text-sm">Mais notícias em breve</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* ===== ÚLTIMAS NOTÍCIAS ===== */}
        {ultimasNoticias.length > 0 && (
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FaFire className="text-orange-500" />
                {categoriaAtiva ? 'Notícias' : 'Últimas Notícias'}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {ultimasNoticias.map((noticia) => (
                <Link
                  key={noticia.id}
                  to={`/mural/${noticia.id}`}
                  className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group"
                >
                  {/* Imagem ou placeholder colorido */}
                  <div className="h-36 overflow-hidden relative">
                    {noticia.imagem_capa ? (
                      <img 
                        src={noticia.imagem_capa} 
                        alt={noticia.titulo}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ backgroundColor: `${noticia.categoria_cor || '#6366f1'}15` }}
                      >
                        {renderCategIcon(noticia.categoria_icone, noticia.categoria_cor || '#6366f1', 'w-10 h-10')}
                      </div>
                    )}
                    <span 
                      className="absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium text-white"
                      style={{ backgroundColor: noticia.categoria_cor || '#6366f1' }}
                    >
                      {noticia.categoria_nome || 'Geral'}
                    </span>
                    {noticia.destaque === 1 && (
                      <span className="absolute top-2 right-2 px-2 py-0.5 rounded text-xs font-medium text-white bg-amber-500 flex items-center gap-1">
                        <FaStar className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                  
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 line-clamp-2 group-hover:text-indigo-600 transition-colors mb-2">
                      {noticia.titulo}
                    </h3>
                    {noticia.subtitulo && (
                      <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                        {noticia.subtitulo}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <FaClock className="w-3 h-3" />
                        {formatarData(noticia.data_publicacao)}
                      </span>
                      <span className="flex items-center gap-1">
                        <FaEye className="w-3 h-3" />
                        {noticia.visualizacoes || 0}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ===== SEÇÕES POR CATEGORIA (só exibe quando não tem filtro) ===== */}
        {!categoriaAtiva && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Coluna Principal - Eventos e Avisos */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Eventos */}
              {eventosNoticias.length > 0 && (
                <section className="bg-white rounded-xl shadow-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <FaCalendarAlt className="text-purple-500" />
                      Eventos
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {eventosNoticias.map((noticia) => (
                      <Link
                        key={noticia.id}
                        to={`/mural/${noticia.id}`}
                        className="flex gap-3 p-3 rounded-lg hover:bg-purple-50 transition-colors group"
                      >
                        <div 
                          className="w-14 h-14 rounded-lg flex-shrink-0 flex flex-col items-center justify-center text-white"
                          style={{ backgroundColor: noticia.categoria_cor || '#8b5cf6' }}
                        >
                          <FaCalendarAlt className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 line-clamp-2 group-hover:text-purple-600 transition-colors">
                            {noticia.titulo}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatarData(noticia.data_publicacao)}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {/* Avisos Gerais */}
              {avisosNoticias.length > 0 && (
                <section className="bg-white rounded-xl shadow-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <FaBullhorn className="text-red-500" />
                      Avisos Gerais
                    </h2>
                  </div>
                  <div className="space-y-3">
                    {avisosNoticias.map((noticia) => (
                      <Link
                        key={noticia.id}
                        to={`/mural/${noticia.id}`}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-red-50 transition-colors group border-l-4 border-red-500"
                      >
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 group-hover:text-red-600 transition-colors">
                            {noticia.titulo}
                          </h4>
                          {noticia.subtitulo && (
                            <p className="text-sm text-gray-500 line-clamp-1 mt-1">
                              {noticia.subtitulo}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {formatarData(noticia.data_publicacao)}
                          </p>
                        </div>
                        <FaChevronRight className="w-4 h-4 text-gray-400 group-hover:text-red-500 transition-colors mt-1" />
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {/* Novidades do Sistema */}
              {novidadesNoticias.length > 0 && (
                <section className="bg-white rounded-xl shadow-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <FaLaptop className="text-blue-500" />
                      Novidades do Sistema
                    </h2>
                  </div>
                  <div className="space-y-2">
                    {novidadesNoticias.map((noticia) => (
                      <Link
                        key={noticia.id}
                        to={`/mural/${noticia.id}`}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-blue-50 transition-colors group"
                      >
                        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"></span>
                        <span className="flex-1 text-gray-700 group-hover:text-blue-600 transition-colors line-clamp-1">
                          {noticia.titulo}
                        </span>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {formatarData(noticia.data_publicacao)}
                        </span>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>

            {/* Sidebar Direita */}
            <div className="space-y-6">
              
              {/* Calendário Acadêmico */}
              {calendarioNoticias.length > 0 && (
                <section className="bg-white rounded-xl shadow-sm p-5">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
                    <FaGraduationCap className="text-green-500" />
                    Calendário Acadêmico
                  </h2>
                  <div className="space-y-3">
                    {calendarioNoticias.map((noticia) => (
                      <Link
                        key={noticia.id}
                        to={`/mural/${noticia.id}`}
                        className="block p-3 rounded-lg bg-green-50 hover:bg-green-100 transition-colors group"
                      >
                        <h4 className="font-medium text-gray-900 group-hover:text-green-700 transition-colors line-clamp-2">
                          {noticia.titulo}
                        </h4>
                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                          <FaCalendarAlt className="w-3 h-3" />
                          {formatarData(noticia.data_publicacao)}
                        </p>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {/* Devocional */}
              {devocionalNoticia && (
                <section className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl shadow-sm p-5 border border-amber-200">
                  <h2 className="text-lg font-bold text-amber-800 flex items-center gap-2 mb-4">
                    <FaBookOpen className="text-amber-600" />
                    Devocional
                  </h2>
                  <Link
                    to={`/mural/${devocionalNoticia.id}`}
                    className="block group"
                  >
                    <h4 className="font-semibold text-amber-900 group-hover:text-amber-700 transition-colors mb-2">
                      {devocionalNoticia.titulo}
                    </h4>
                    {devocionalNoticia.subtitulo && (
                      <p className="text-amber-700 text-sm italic line-clamp-3">
                        "{devocionalNoticia.subtitulo}"
                      </p>
                    )}
                    <p className="text-xs text-amber-600 mt-3 flex items-center gap-1">
                      <FaClock className="w-3 h-3" />
                      {formatarData(devocionalNoticia.data_publicacao)}
                    </p>
                  </Link>
                </section>
              )}

              {/* Mais Lidas */}
              {maisLidasNoticias.length > 0 && (
                <section className="bg-white rounded-xl shadow-sm p-5">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
                    <FaFire className="text-orange-500" />
                    Mais Lidas
                  </h2>
                  <div className="space-y-3">
                    {maisLidasNoticias.map((noticia, index) => (
                        <Link
                          key={noticia.id}
                          to={`/mural/${noticia.id}`}
                          className="flex items-start gap-3 group"
                        >
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                            index === 0 ? 'bg-amber-500 text-white' :
                            index === 1 ? 'bg-gray-400 text-white' :
                            index === 2 ? 'bg-amber-700 text-white' :
                            'bg-gray-200 text-gray-600'
                          }`}>
                            {index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-indigo-600 transition-colors">
                              {noticia.titulo}
                            </h4>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {noticia.visualizacoes || 0} visualizações
                            </p>
                          </div>
                        </Link>
                      ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        )}

        {/* Mensagem se não houver notícias */}
        {!portalData && noticias.length === 0 && !loading && (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <FaNewspaper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              {categoriaAtiva || searchTerm ? 'Nenhuma notícia encontrada' : 'Nenhuma notícia publicada'}
            </h3>
            <p className="text-gray-500">
              {categoriaAtiva || searchTerm 
                ? 'Tente ajustar os filtros de busca' 
                : 'Em breve teremos novidades para você!'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MuralNoticias;
